import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { ArticleImagesEnum } from 'src/app/enum/article-image-enums';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { ArticleModel } from 'src/app/models/archive/article.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-attach-images-article',
  standalone: false,
  templateUrl: './attach-images-article.component.html',
  styleUrls: ['./attach-images-article.component.scss']
})
export class AttachImagesArticleComponent implements OnInit {

  articleContract: ArticleModel = new ArticleModel();
  disabledForm: boolean = false;
  imageTitles: string[] = ['Imagen 1', 'Imagen 2', 'Imagen 3'];
  permissionUpdatable: boolean = false;
  showButtons: boolean = true;
  previews: (SafeUrl | null)[] = [null, null, null];
  subscription: Subscription;
  listImagesSelection: CustomListDropdown[] = [];
  mainImage: FormControl = new FormControl();

  constructor(private articleService: ArticleService,
    private config: DynamicDialogConfig,
    private confirmDialogService: ConfirmDialogService,
    private sanitizer: DomSanitizer,
    private toastHandlerService: ToastHandlerService) { }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.articleContract = inputs.articleContract;
      this.permissionUpdatable = inputs.permissionUpdatable;
      this.disabledForm = inputs.disabledForm;
      this.showButtons = inputs.showButtons;
      this.mainImage.patchValue(this.articleContract.mainImage);
    });
    if (this.showButtons) {
      await this.chargeImages();
    } else {
      await this.chargeImagesForShow();
    }
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  async changeSelected(event: any) {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para modificar la imagen pricipal del artículo. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    try {
      const articleId = this.articleContract.articleId;
      this.articleContract = await firstValueFrom(
        this.articleService.selectMainImageAsync(articleId, event)
      );
      this.emitArticleContract(this.articleContract);
      this.toastHandlerService.showToast('success', "Imagen principal actualizada correctamente.");
    } catch (error) {
      console.error('Error al cambiar la imagen principal.', error);
      this.toastHandlerService.showToast(
        'error',
        'Error al cambiar la imagen principal.'
      );
    }
  }

  async attachImage(image: File, slotIndex: number) {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para modificar artículos. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    const configMap: Record<number, { successMessage: string }> = {
      [ArticleImagesEnum.imagen1]: {
        successMessage: 'La imagen 1 subió correctamente.',
      },
      [ArticleImagesEnum.imagen2]: {
        successMessage: 'La imagen 2 se subió correctamente.',
      },
      [ArticleImagesEnum.imagen3]: {
        successMessage: 'La imagen 3 se subió correctamente.',
      },
    };
    const config = configMap[slotIndex];
    const formData = new FormData();
    formData.append('file', image);
    try {
      const articleId = this.articleContract.articleId;
      this.articleContract = await firstValueFrom(
        this.articleService.attachImage(articleId, slotIndex, formData)
      );
      this.mainImage.patchValue(this.articleContract.mainImage);
      this.emitArticleContract(this.articleContract);
      this.toastHandlerService.showToast('success', config.successMessage);
    } catch (error) {
      console.error('Error al subir la imagen.', error);
      this.toastHandlerService.showToast(
        'error',
        'Error al subir la imagen.'
      );
    }
  }

  async attachImageUrl(imageUrl: string, slotIndex: number) {
    try {
      this.previews[slotIndex - 1] = imageUrl;
      this.chargeListImagesSelection();
    } catch (error) {
      this.toastHandlerService.showToast(
        'error',
        'Error al subir la imagen.'
      );
      console.error(error);
    }
  }

  async buildImageUrl(imagePath: string): Promise<SafeUrl | null> {
    try {
      const blob = await firstValueFrom(this.articleService.getImageUrl(imagePath));
      const objectURL = URL.createObjectURL(blob);
      return this.sanitizer.bypassSecurityTrustUrl(objectURL);
    } catch (error) {
      console.error("Error cargando la imagen.", error);
      return null;
    }
  }

  async chargeImages() {
    const imagePaths = [
      this.articleContract.image1,
      this.articleContract.image2,
      this.articleContract.image3,
    ];

    for (let i = 0; i < imagePaths.length; i++) {
      if (imagePaths[i]) {
        this.previews[i] = await this.buildImageUrl(imagePaths[i]!);
        this.chargeListImagesSelection();
      }
    }

  }

  chargeListImagesSelection() {
    this.listImagesSelection = [];
    for (let i = 0; i < this.previews.length; i++) {
      if (this.previews[i]) {
        this.listImagesSelection.push({
          value: i + 1,
          label: `${this.imageTitles[i]}`,
        });
      }
    }
  }

  async chargeImagesForShow() {
    const imagePaths = [
      this.articleContract.image1,
      this.articleContract.image2,
      this.articleContract.image3,
    ].filter(Boolean) as string[];  // solo los que tienen URL
    const urls = await Promise.all(
      imagePaths.map(path => this.buildImageUrl(path))
    );
    this.previews = urls.filter(url => url !== null) as SafeUrl[];
  }

  async deleteImage(slotIndex: number) {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para modificar empresas. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    const configMap: Record<number, { message: string; successMessage: string }> = {
      [ArticleImagesEnum.imagen1]: {
        message: '¿Realmente desea limpiar la imagen 1?',
        successMessage: 'La imagen 1 se eliminó correctamente.',
      },
      [ArticleImagesEnum.imagen2]: {
        message: '¿Realmente desea limpiar la imagen 2?',
        successMessage: 'La imagen 2 del footer se eliminó correctamente.',
      },
      [ArticleImagesEnum.imagen3]: {
        message: '¿Realmente desea limpiar la imagen 3?',
        successMessage: 'La imagen 3 se eliminó correctamente.',
      },
    };
    const config = configMap[slotIndex];
    if (config) {
      try {
        if (await this.confirmDialogService.confirm(config.message)) {
          const articleId = this.articleContract.articleId;
          this.articleContract = await firstValueFrom(this.articleService.deleteImage(articleId, slotIndex));
          this.previews[slotIndex - 1] = null;
          this.chargeListImagesSelection();
          this.emitArticleContract(this.articleContract);
          this.toastHandlerService.showToast('success', config.successMessage);
        }
      } catch (error) {
        console.error('Error al eliminar la imagen.', error);
      }
    }
  }

  emitArticleContract(articleContract: ArticleModel) {
    const output$ = this.config.data?.output$;
    output$?.next({ type: 'updateArticleContract', payload: articleContract });
  }
}